import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import axios from 'axios';
import { BASE_URL } from '../../constants';
import '../ForBrands/brandMode.css';
import {
  Badge,
  BmModal,
  Btn,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  SearchBox,
  SegmentedControl,
  Select,
} from '../ForBrands/ui/BrandUI';
import { Icon } from '../ForBrands/ui/icons';
import {
  DANGER_SOLID,
  PaymentStatusBadge,
  PaymentsPager,
  TD_CLASS,
  TD_MUTED_CLASS,
  TH_CLASS,
  TR_CLASS,
} from './paymentStatus';
import { fetchUserPlans } from './userPlans';
import { includedKind, includedTitle } from '../common/PlanBadge';
import { displayTeamName } from '../../utils/teamNames';

const PLANS_URL = `${BASE_URL}/api/users/admin/team-plans/`;

// Which verification codes include Premium on their own (the server's code
// rule, users/team_plans.py). A team plan always does.
export const CODE_RULES = [
  { value: 'staff_codes', label: 'CACHE-issued codes waive it',
    hint: "A code issued from Team codes or a team request makes the player Premium. A coach's code follows the coach's plan." },
  { value: 'all_codes', label: 'Every verification code waives it',
    hint: "Any team or coach code makes the player Premium, whatever the coach's plan." },
  { value: 'sponsored_only', label: 'Only sponsored teams',
    hint: 'Codes waive nothing. Players are Premium only on a team with a team plan.' },
];

const SUB_TABS = [
  { value: 'coaches', label: 'Coaches' },
  { value: 'athletes', label: 'Athletes' },
];

const DURATIONS = [
  { value: 'week', label: '1 week (7 days)' },
  { value: 'month', label: '1 month (30 days)' },
  { value: 'custom', label: 'Custom' },
];

// The type-the-email check: case-insensitive, and never armed for a row with
// no email.
const emailMatches = (typed, email) => !!email && String(typed || '').toLowerCase() === String(email).toLowerCase();

// An active plan with neither a Stripe subscription nor a manual flag: the
// legacy rows the Reset to free cleanup is for.
const isLegacyUser = (user) => {
  const status = user.subscription?.status;
  const hasActiveStripeSubscription = user.stripeSubscriptionId && status === 'active';
  const isManual = user.subscription?.manuallyApplied || user.subscription?.manuallyGranted;
  return status === 'active' && !hasActiveStripeSubscription && !isManual;
};

// The Current tier and Status cells from the plan the server resolves for
// the row (POST admin/users/plans/), or null to read the stored fields as
// before (the plans have not loaded, or could not be read). The stored
// fields miss two things (Kobi, 2026-10-07):
//   - an athlete their team covers has Premium and no tier of their own:
//     "Premium (team plan) · <team>" for a coach's plan or a team plan,
//     "Premium (team code) · <team>" for a team code that waives the fee,
//     status Included, its title naming the coach or the sponsor (the team
//     is the athlete's squad as the server names it: with its age group);
//   - a plan CACHE set up past its end date, or an access grant past its
//     end, still says active or trialing: No tier, status Ended.
// A coach's tier reads as the server names it (Core, Pro, Premium Team).
// `status` null keeps the stored status badge. The plan is read only when
// it is for the segment's role (`role`: 'coach' or 'athlete'): the server
// resolves an account with both roles as an athlete, so its coach tier keeps
// the stored fields on Coaches.
const TEAM_SOURCES = ['coach', 'team_plan', 'team_code'];
export const planCells = (user, plan, role) => {
  if (!plan || !plan.entitlement || (role && plan.role !== role)) return null;
  if (plan.entitlement === 'athlete_premium' && TEAM_SOURCES.includes(plan.source)) {
    const word = includedKind(plan) === 'team_code' ? 'Premium (team code)' : 'Premium (team plan)';
    const team = String(plan.coverage?.teamName || plan.coverage?.sponsorName || '').trim();
    return {
      tier: <Badge tone="soft">{team ? `${word} · ${team}` : word}</Badge>,
      status: <span title={includedTitle(plan.coverage)}><PaymentStatusBadge status="active" label="Included" /></span>,
    };
  }
  if (!plan.label || plan.label === 'Free') {
    const stored = user.subscription?.status;
    const claimsTier = !!user.subscriptionTier && user.subscriptionTier !== 'free';
    const ended = claimsTier && (stored === 'active' || stored === 'trialing');
    return {
      tier: <span className="text-bm-muted">No tier</span>,
      status: ended ? (
        <span title="The plan's end date has passed, so it no longer gives access.">
          <PaymentStatusBadge status="expired" label="Ended" />
        </span>
      ) : null,
    };
  }
  return { tier: <Badge tone="soft">{plan.label}</Badge>, status: null };
};

// The note under a dialog: what the action does to the account.
const Notice = ({ children, critical = false }) => (
  <div className="flex gap-2 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3">
    <Icon name="alert-circle" size={16} className={`mt-0.5 ${critical ? 'text-bm-critical' : 'text-bm-muted'}`} />
    <div className="bm-body-s text-bm-muted">{children}</div>
  </div>
);

// A plan's last day ("2027-06-30") as "Jun 30, 2027".
export const planDay = (day) => {
  const when = day ? new Date(`${day}T00:00:00Z`) : null;
  if (!when || Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// "This squad", or "Whole program · 17U, 16U, 15U".
export const planScopeLine = (plan) => {
  if (plan?.scope !== 'program') return 'This squad';
  const ages = (plan.squads || []).map((s) => s?.ageGroup).filter(Boolean);
  return ages.length ? `Whole program · ${ages.join(', ')}` : 'Whole program';
};

const planTeam = (plan) => displayTeamName({ teamName: plan?.teamName, league: plan?.league }) || plan?.teamId || 'Team';

// Payments > Manage tiers, Athletes: which verification codes waive the
// athlete fee (the code rule) and the teams CACHE sponsors (team plans),
// from admin/team-plans/ (users/team_plans_views.py). Both apply to every
// athlete on their next read; nothing is stored on the athletes.
export const FeeWaivers = ({ token }) => {
  const [rule, setRule] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [ending, setEnding] = useState(null);
  const titleId = useId();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(PLANS_URL, auth);
      setRule(res.data?.codeRule || 'staff_codes');
      setPlans(Array.isArray(res.data?.plans) ? res.data.plans : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load the team plans.');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const saveRule = async (value) => {
    if (value === rule || saving) return;
    const before = rule;
    setRule(value);
    setSaving(true);
    try {
      const res = await axios.post(PLANS_URL, { action: 'rule', codeRule: value }, auth);
      setRule(res.data?.codeRule || value);
      toast.success('Saved. Every athlete reads the new rule right away.');
    } catch (err) {
      setRule(before);
      toast.error(err?.response?.data?.message || 'Could not save the rule.');
    } finally {
      setSaving(false);
    }
  };

  const endPlan = async () => {
    const plan = ending;
    setEnding(null);
    try {
      await axios.post(PLANS_URL, { action: 'end', planId: plan.id }, auth);
      toast.success(`The team plan for ${planTeam(plan)} has ended.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not end the team plan.');
    }
  };

  return (
    <Card padding="p-6" className="flex flex-col gap-5">
      <div>
        <h3 id={titleId} className="bm-h-xs text-bm-ink">Verification codes and the athlete fee</h3>
        <p className="mt-1 bm-body-s text-bm-muted">
          Which codes make a player Premium without paying. A coach on a paid plan and a team plan always do.
        </p>
      </div>
      {loading ? <Loading label="Loading team plans..." /> : error ? <ErrorState>{error}</ErrorState> : (
        <>
          <fieldset className="flex flex-col gap-2" disabled={saving} aria-labelledby={titleId}>
            {CODE_RULES.map((opt) => {
              const active = rule === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-[8px] border px-4 py-3 transition-colors ${
                    active ? 'border-bm-orange bg-bm-orange/5' : 'border-bm-border bg-white hover:bg-bm-subtle'
                  }`}
                >
                  <input
                    type="radio"
                    name="code-rule"
                    value={opt.value}
                    checked={active}
                    onChange={() => saveRule(opt.value)}
                    className="mt-1 accent-bm-orange"
                  />
                  <span className="min-w-0">
                    <span className="block bm-label-l text-bm-ink">{opt.label}</span>
                    <span className="mt-0.5 block bm-body-s text-bm-muted">{opt.hint}</span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          <div className="flex flex-col gap-2">
            <span className="bm-overline text-bm-muted">Team plans</span>
            {plans.length === 0 ? (
              <p className="bm-body-s text-bm-muted">No team plans yet. Turn one on from Team codes on the Teams page.</p>
            ) : (
              <div className="w-full overflow-x-auto rounded-[8px] border border-bm-border">
                <table className="w-full text-left" aria-label="Team plans">
                  <thead className="bg-bm-subtle">
                    <tr>
                      <th className={TH_CLASS}>Team</th>
                      <th className={TH_CLASS}>Sponsor</th>
                      <th className={TH_CLASS}>Players</th>
                      <th className={TH_CLASS}>Ends</th>
                      <th className={TH_CLASS}>Status</th>
                      <th className={TH_CLASS}><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => {
                      const live = plan.status === 'active';
                      return (
                        <tr key={plan.id} className={TR_CLASS}>
                          <td className={TD_CLASS}>
                            <div className="bm-label-l text-bm-ink">{planTeam(plan)}</div>
                            <div className="bm-body-s text-bm-muted">{planScopeLine(plan)}</div>
                          </td>
                          <td className={TD_MUTED_CLASS}>{plan.sponsorLabel || planTeam(plan)}</td>
                          <td className={TD_MUTED_CLASS}>{live ? plan.covered ?? 0 : '-'}</td>
                          <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>{planDay(plan.endsOn) || 'No end date'}</td>
                          <td className={TD_CLASS}>
                            <PaymentStatusBadge status={live ? 'active' : 'expired'} label={live ? 'Active' : 'Ended'} />
                          </td>
                          <td className={TD_CLASS}>
                            {live && (
                              <Btn variant="danger" size="sm" onClick={() => setEnding(plan)}>End plan</Btn>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {ending && (
        <ConfirmModal
          tone="danger"
          title="End this team plan?"
          body={`Players on ${planTeam(ending)} lose the Premium it includes right away, unless a coach's plan or a code still covers them.`}
          confirmLabel="End plan"
          cancelLabel="Keep it"
          requireAck={false}
          onConfirm={endPlan}
          onClose={() => setEnding(null)}
        />
      )}
    </Card>
  );
};

const ManageTiersTab = () => {
  const token = useSelector((state) => state.auth.token);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [processingUser, setProcessingUser] = useState(null);
  const [actionModal, setActionModal] = useState({ isOpen: false, user: null, action: null });
  const [activeSubTab, setActiveSubTab] = useState('coaches'); // 'coaches' or 'athletes'
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 20;
  const [cleanupModal, setCleanupModal] = useState({ isOpen: false, user: null });
  const [emailConfirmation, setEmailConfirmation] = useState('');
  // The plan of each row on screen, as the server resolves it (planCells).
  const [plans, setPlans] = useState({});

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const response = await axios.get(
        `${BASE_URL}/api/users/users/whole/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Ensure response.data.users is an array - get both coaches and athletes
      const userData = Array.isArray(response.data.users) ? response.data.users : [];
      setUsers(userData);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
      setLoadError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const getTierDisplayName = (tier) => {
    const tierNames = {
      'COACH_CORE': 'Core',
      'COACH_PRO': 'Pro',
      'COACH_PREMIUM': 'Premium',
      'ATHLETE_PREMIUM': 'Premium'
    };
    return tierNames[tier] || tier;
  };

  const getStatusBadge = (user) => {
    const status = user.subscription?.status;
    const hasActiveStripeSubscription = user.stripeSubscriptionId && status === 'active';
    const isManual = user.subscription?.manuallyApplied || user.subscription?.manuallyGranted;

    if (status === 'active') {
      if (hasActiveStripeSubscription) return <PaymentStatusBadge status="active" label="Active (Stripe)" />;
      if (isManual) return <PaymentStatusBadge status="active" label="Active (Manual)" />;
      return <PaymentStatusBadge status="legacy" />;
    }
    if (status === 'trialing') return <PaymentStatusBadge status="trialing" />;
    if (status === 'canceled' || status === 'canceling') return <PaymentStatusBadge status="canceled" />;
    return <PaymentStatusBadge status="free" />;
  };

  const handleRemoveTier = (user) => {
    setActionModal({ isOpen: true, user, action: 'remove_tier' });
  };

  const handleApplyTier = (user) => {
    setActionModal({ isOpen: true, user, action: 'apply_tier' });
  };

  const handleGrantCoupon = (user) => {
    setActionModal({ isOpen: true, user, action: 'grant_coupon' });
  };

  const handleRemoveCoupon = (user) => {
    setActionModal({ isOpen: true, user, action: 'remove_coupon' });
  };

  const confirmRemoveTier = async () => {
    const user = actionModal.user;
    setActionModal({ isOpen: false, user: null, action: null });

    try {
      setProcessingUser(user.email);

      await axios.post(
        `${BASE_URL}/api/users/manage-tier/remove/`,
        { user_email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Tier removed for ${user.email}`);
      fetchUsers();
    } catch (error) {
      console.error('Error removing tier:', error);
      toast.error(error.response?.data?.error || 'Failed to remove tier');
    } finally {
      setProcessingUser(null);
    }
  };

  const confirmApplyTier = async (tier) => {
    const user = actionModal.user;
    setActionModal({ isOpen: false, user: null, action: null });

    try {
      setProcessingUser(user.email);

      await axios.post(
        `${BASE_URL}/api/users/manage-tier/apply/`,
        {
          user_email: user.email,
          tier: tier
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${getTierDisplayName(tier)} tier applied to ${user.email}`);
      fetchUsers();
    } catch (error) {
      console.error('Error applying tier:', error);
      toast.error(error.response?.data?.error || 'Failed to apply tier');
    } finally {
      setProcessingUser(null);
    }
  };

  const confirmGrantCoupon = async (tier, duration, customDays) => {
    const user = actionModal.user;
    setActionModal({ isOpen: false, user: null, action: null });

    try {
      setProcessingUser(user.email);

      // Calculate days based on duration
      let days;
      if (duration === 'custom') {
        days = parseInt(customDays);
      } else if (duration === 'week') {
        days = 7;
      } else if (duration === 'month') {
        days = 30;
      }

      await axios.post(
        `${BASE_URL}/api/users/manage-tier/grant-trial/`,
        {
          user_email: user.email,
          tier: tier,
          trial_days: days
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const durationText = duration === 'custom' ? `${days}-day` : duration === 'week' ? '1-week' : '1-month';
      toast.success(`${durationText} access granted for ${getTierDisplayName(tier)} to ${user.email}`);
      fetchUsers();
    } catch (error) {
      console.error('Error granting coupon:', error);
      toast.error(error.response?.data?.error || 'Failed to grant access');
    } finally {
      setProcessingUser(null);
    }
  };

  const confirmRemoveCoupon = async () => {
    const user = actionModal.user;
    setActionModal({ isOpen: false, user: null, action: null });

    try {
      setProcessingUser(user.email);

      await axios.post(
        `${BASE_URL}/api/users/manage-tier/remove-trial/`,
        { user_email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Temporary access removed for ${user.email}`);
      fetchUsers();
    } catch (error) {
      console.error('Error removing coupon:', error);
      toast.error(error.response?.data?.error || 'Failed to remove access');
    } finally {
      setProcessingUser(null);
    }
  };

  const handleResetToFree = (user) => {
    setCleanupModal({ isOpen: true, user });
    setEmailConfirmation('');
  };

  const closeCleanupModal = () => {
    setCleanupModal({ isOpen: false, user: null });
    setEmailConfirmation('');
  };

  const confirmResetToFree = async () => {
    const user = cleanupModal.user;

    if (emailConfirmation.toLowerCase() !== user.email.toLowerCase()) {
      toast.error('Email confirmation does not match');
      return;
    }

    setCleanupModal({ isOpen: false, user: null });
    setEmailConfirmation('');

    try {
      setProcessingUser(user.email);

      await axios.post(
        `${BASE_URL}/api/users/manage-tier/remove/`,
        { user_email: user.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Legacy data cleaned - ${user.email} reset to free tier`);
      fetchUsers();
    } catch (error) {
      console.error('Error resetting to free:', error);
      toast.error(error.response?.data?.error || 'Failed to reset user to free tier');
    } finally {
      setProcessingUser(null);
    }
  };

  // Reset to page 1 when switching tabs or filters
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSubTab, searchQuery, tierFilter, statusFilter]);

  // Filter users by role (coaches or athletes)
  const roleFilteredUsers = users.filter(user => {
    if (activeSubTab === 'coaches') {
      return user.permissions?.includes('IS_COACH');
    } else {
      return user.permissions?.includes('IS_ATHLETE');
    }
  });

  // Apply search and filter
  const filteredUsers = roleFilteredUsers.filter(user => {
    const matchesSearch = searchQuery.trim() === '' ||
      user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTier = tierFilter === 'all' || user.subscriptionTier === tierFilter;

    const matchesStatus = statusFilter === 'all' || user.subscription?.status === statusFilter;

    return matchesSearch && matchesTier && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const endIndex = startIndex + USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // The plans of the rows on this page, read again whenever the list
  // reloads (after every tier action), so the cells follow the change.
  const pageEmails = paginatedUsers.map((u) => u.email).filter(Boolean).sort().join('\n');
  useEffect(() => {
    if (!pageEmails) return undefined;
    let live = true;
    Promise.resolve(fetchUserPlans(pageEmails.split('\n'), token)).then((found) => {
      if (live && found && Object.keys(found).length) setPlans((prev) => ({ ...prev, ...found }));
    }).catch(() => {});
    return () => { live = false; };
  }, [pageEmails, users, token]);

  const renderBody = () => {
    if (loading) return <Loading label="Loading users..." />;
    if (loadError) return <ErrorState>{loadError}</ErrorState>;
    if (filteredUsers.length === 0) return <EmptyState>No users match your filters.</EmptyState>;
    return (
      <>
        <Card padding="p-0" className="w-full min-w-0 max-w-full overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-bm-subtle">
                <tr>
                  <th className={TH_CLASS}>User</th>
                  <th className={TH_CLASS}>Current tier</th>
                  <th className={TH_CLASS}>Status</th>
                  <th className={TH_CLASS}>Subscription end</th>
                  <th className={TH_CLASS}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => {
                  const isProcessing = processingUser === user.email;
                  const isTrialing = user.subscription?.status === 'trialing';
                  const isActive = user.subscription?.status === 'active';
                  // Only show tier if user has an active or trialing subscription
                  const hasTier = (isActive || isTrialing) && user.subscriptionTier && user.subscriptionTier !== 'free';
                  // Only show subscription end date if user has an active or trialing subscription
                  const subscriptionEnd = (isActive || isTrialing) ? (user.subscription?.endDate || user.subscription?.trialEnd) : null;
                  // The server's plan, when it has loaded (planCells).
                  const cells = planCells(user, plans[user.email], activeSubTab === 'coaches' ? 'coach' : 'athlete');

                  return (
                    <tr key={user.email} className={TR_CLASS}>
                      <td className={TD_CLASS}>
                        <div className="bm-label-l text-bm-ink">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="bm-body-s text-bm-muted">{user.email}</div>
                      </td>

                      <td className={TD_CLASS}>
                        {cells ? cells.tier : hasTier ? (
                          <Badge tone="soft">{getTierDisplayName(user.subscriptionTier)}</Badge>
                        ) : (
                          <span className="text-bm-muted">No tier</span>
                        )}
                      </td>

                      <td className={TD_CLASS}>{cells?.status || getStatusBadge(user)}</td>

                      <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>
                        {subscriptionEnd
                          ? new Date(subscriptionEnd).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                          : <span className="text-bm-tertiary">-</span>}
                      </td>

                      <td className={TD_CLASS}>
                        <div className="flex flex-wrap gap-2">
                          {isLegacyUser(user) && (
                            <Btn variant="danger" size="sm" className="gap-1.5" onClick={() => handleResetToFree(user)} disabled={isProcessing}>
                              <Icon name="refresh" size={14} />
                              Reset to free
                            </Btn>
                          )}

                          {hasTier && (
                            <Btn variant="danger" size="sm" className="gap-1.5" onClick={() => handleRemoveTier(user)} disabled={isProcessing}>
                              <Icon name="trash" size={14} />
                              Remove tier
                            </Btn>
                          )}

                          <Btn variant="secondary" size="sm" className="gap-1.5" onClick={() => handleApplyTier(user)} disabled={isProcessing}>
                            <Icon name="plus-circle" size={14} />
                            {hasTier ? 'Update tier' : 'Apply tier'}
                          </Btn>

                          {!hasTier && (
                            isTrialing ? (
                              <Btn variant="danger" size="sm" className="gap-1.5" onClick={() => handleRemoveCoupon(user)} disabled={isProcessing}>
                                <Icon name="x" size={14} />
                                End access
                              </Btn>
                            ) : (
                              <Btn variant="secondary" size="sm" className="gap-1.5" onClick={() => handleGrantCoupon(user)} disabled={isProcessing}>
                                <Icon name="calendar" size={14} />
                                Grant access
                              </Btn>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="bm-body-s text-bm-muted">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </div>
            <PaymentsPager page={currentPage} pageCount={totalPages} onChange={setCurrentPage} />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="bm-h-s text-bm-ink">Manage tiers</h2>
        <SegmentedControl value={activeSubTab} onChange={setActiveSubTab} options={SUB_TABS} capitalize={false} />
      </div>

      {activeSubTab === 'athletes' && <FeeWaivers token={token} />}

      <Card padding="p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Search">
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Name or email..."
              aria-label="Search users by name or email"
            />
          </Field>
          <Field label="Tier">
            <Select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
              <option value="all">All tiers</option>
              {activeSubTab === 'coaches' ? (
                <>
                  <option value="COACH_CORE">Core</option>
                  <option value="COACH_PRO">Pro</option>
                  <option value="COACH_PREMIUM">Premium</option>
                </>
              ) : (
                <option value="ATHLETE_PREMIUM">Premium</option>
              )}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="trialing">Trialing</option>
              <option value="canceled">Canceled</option>
              <option value="free">Free</option>
            </Select>
          </Field>
        </div>
      </Card>

      {renderBody()}

      {actionModal.isOpen && (
        <ActionModal
          user={actionModal.user}
          action={actionModal.action}
          forAthletes={activeSubTab === 'athletes'}
          onClose={() => setActionModal({ isOpen: false, user: null, action: null })}
          onConfirmRemoveTier={confirmRemoveTier}
          onConfirmApplyTier={confirmApplyTier}
          onConfirmGrantCoupon={confirmGrantCoupon}
          onConfirmRemoveCoupon={confirmRemoveCoupon}
          getTierDisplayName={getTierDisplayName}
        />
      )}

      {cleanupModal.isOpen && (
        <BmModal
          title="Reset legacy user to free"
          onClose={closeCleanupModal}
          footer={
            <>
              <Btn variant="secondary" className="flex-1" onClick={closeCleanupModal}>
                Cancel
              </Btn>
              <Btn
                variant="tertiary"
                className={`flex-1 ${DANGER_SOLID}`}
                onClick={confirmResetToFree}
                disabled={!emailMatches(emailConfirmation, cleanupModal.user?.email)}
              >
                Reset to free
              </Btn>
            </>
          }
        >
          <p className="bm-body-m text-bm-muted">
            This will remove all subscription data for <span className="bm-label-l text-bm-ink">{cleanupModal.user?.email}</span>
          </p>
          <div className="mt-4">
            <Notice critical>
              <p className="bm-label-m text-bm-ink">This action will:</p>
              <ul className="mt-1 list-inside list-disc">
                <li>Reset user to free tier</li>
                <li>Remove all subscription data</li>
                <li>Clear Stripe customer information</li>
              </ul>
            </Notice>
          </div>
          <Field className="mt-4" label="Type the user's email to confirm" hint={cleanupModal.user?.email}>
            <Input
              type="text"
              value={emailConfirmation}
              onChange={(e) => setEmailConfirmation(e.target.value)}
              placeholder="Enter email to confirm"
              autoFocus
            />
          </Field>
        </BmModal>
      )}
    </div>
  );
};

// Action Modal Component. The tiers offered follow the segment the row is
// listed under: the athlete plan on Athletes, the coach plans on Coaches (an
// account with both roles shows on both).
const ActionModal = ({ user, action, forAthletes = false, onClose, onConfirmRemoveTier, onConfirmApplyTier, onConfirmGrantCoupon, onConfirmRemoveCoupon, getTierDisplayName }) => {
  const isCoach = !forAthletes && user.permissions?.includes('IS_COACH');
  const [selectedTier, setSelectedTier] = useState(isCoach ? 'COACH_CORE' : 'ATHLETE_PREMIUM');
  const [duration, setDuration] = useState('week');
  const [customDays, setCustomDays] = useState('7');
  const [emailConfirmation, setEmailConfirmation] = useState('');

  // Ending temporary access revokes it at once: a danger ConfirmModal.
  if (action === 'remove_coupon') {
    return (
      <ConfirmModal
        tone="danger"
        title="End temporary access"
        body={`Are you sure you want to end temporary access for ${user.email}? This will immediately end their access and revert them to free tier.`}
        confirmLabel="End access"
        onConfirm={onConfirmRemoveCoupon}
        onClose={onClose}
      />
    );
  }

  const tierSelect = (
    <Field label="Select tier">
      <Select value={selectedTier} onChange={(e) => setSelectedTier(e.target.value)}>
        {isCoach ? (
          <>
            <option value="COACH_CORE">Core Subscription</option>
            <option value="COACH_PRO">Pro Subscription</option>
            <option value="COACH_PREMIUM">Premium Subscription</option>
          </>
        ) : (
          <option value="ATHLETE_PREMIUM">Premium Subscription</option>
        )}
      </Select>
    </Field>
  );

  const footer = (confirmLabel, onConfirm, { danger = false, disabled = false } = {}) => (
    <>
      <Btn variant="secondary" className="flex-1" onClick={onClose}>
        Cancel
      </Btn>
      <Btn
        variant={danger ? 'tertiary' : 'primary'}
        className={`flex-1 ${danger ? DANGER_SOLID : ''}`}
        onClick={onConfirm}
        disabled={disabled}
      >
        {confirmLabel}
      </Btn>
    </>
  );

  if (action === 'remove_tier') {
    return (
      <BmModal
        title="Remove tier"
        onClose={onClose}
        footer={footer('Remove tier', onConfirmRemoveTier, {
          danger: true,
          disabled: !emailMatches(emailConfirmation, user.email),
        })}
      >
        <p className="bm-body-m text-bm-muted">
          You are about to remove the {getTierDisplayName(user.subscriptionTier)} tier from:
        </p>
        <p className="mt-1 bm-label-l text-bm-ink">{user.email}</p>
        <div className="mt-4">
          <Notice critical>This will immediately revoke their access to all tier features.</Notice>
        </div>
        <Field className="mt-4" label="Type the user's email to confirm">
          <Input
            type="text"
            value={emailConfirmation}
            onChange={(e) => setEmailConfirmation(e.target.value)}
            placeholder="Enter email to confirm"
            autoFocus
          />
        </Field>
      </BmModal>
    );
  }

  if (action === 'apply_tier') {
    const hasExistingTier = user.subscriptionTier && user.subscriptionTier !== 'free';
    const label = hasExistingTier ? 'Update tier' : 'Apply tier';
    return (
      <BmModal title={label} onClose={onClose} footer={footer(label, () => onConfirmApplyTier(selectedTier))}>
        <p className="bm-body-m text-bm-muted">
          {hasExistingTier
            ? `Update tier for ${user.email}. Current tier: ${getTierDisplayName(user.subscriptionTier)}. Select new tier:`
            : `Select a tier to apply to ${user.email}:`}
        </p>
        <div className="mt-4">{tierSelect}</div>
        {hasExistingTier && (
          <div className="mt-4">
            <Notice>This will replace their current subscription with a new 1-year subscription.</Notice>
          </div>
        )}
      </BmModal>
    );
  }

  if (action === 'grant_coupon') {
    return (
      <BmModal
        title="Grant temporary access"
        onClose={onClose}
        footer={footer('Grant access', () => onConfirmGrantCoupon(selectedTier, duration, customDays))}
      >
        <p className="bm-body-m text-bm-muted">Grant temporary access to {user.email}:</p>
        <div className="mt-4">{tierSelect}</div>
        <div className="mt-4 flex flex-col gap-[6px]">
          <span className="bm-label-l text-bm-ink">Duration</span>
          {/* The SegmentedControl marks the picked option with aria-pressed.
              On a phone-width modal the row scrolls instead of overflowing. */}
          <div role="group" aria-label="Duration" className="max-w-full overflow-x-auto whitespace-nowrap">
            <SegmentedControl value={duration} onChange={setDuration} options={DURATIONS} capitalize={false} />
          </div>
          {duration === 'custom' && (
            <Input
              type="number"
              min="1"
              max="365"
              aria-label="Custom days"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              placeholder="Enter days"
              className="mt-2 max-w-[200px]"
            />
          )}
        </div>
        <div className="mt-4">
          <Notice>Access will automatically expire after the selected duration and revert to free tier.</Notice>
        </div>
      </BmModal>
    );
  }

  return null;
};

export default ManageTiersTab;
